<template>
  <div class="container">
    <StatsTabs />

    <!-- 헤더 -->
    <div class="go-header">
      <h1 class="go-title">📈 성장 대시보드</h1>
      <p class="go-subtitle">Linkook Live 사용자 성장 · 참여 · 전환 지표를 한눈에 확인하세요.</p>
    </div>

    <!-- 날짜 필터 바 -->
    <div class="filter-bar">
      <div class="filter-presets">
        <button v-for="p in presets" :key="p.key" :class="['preset-btn', { active: activePreset === p.key }]"
          @click="applyPreset(p.key)">
          {{ p.label }}
        </button>
      </div>

      <div class="filter-sep" />

      <div class="filter-monthly">
        <select v-model="selectedMonth" @change="applyMonth" class="filter-select">
          <option value="">월 선택</option>
          <option v-for="m in monthOptions" :key="m.value" :value="m.value">
            {{ m.label }}
          </option>
        </select>
      </div>

      <div class="filter-custom">
        <input type="date" v-model="customStart" class="date-input" :max="customEnd || today" />
        <span class="range-sep">–</span>
        <input type="date" v-model="customEnd" class="date-input" :min="customStart" :max="today" />
        <button class="apply-button" @click="applyCustom" :disabled="!customStart || !customEnd">
          적용
        </button>
      </div>
    </div>

    <!-- 조회 기간 표시 -->
    <div class="period-badge">{{ periodLabel }}</div>

    <!-- 요약 카드 -->
    <div class="cards-grid">
      <div v-for="card in summaryCards" :key="card.key" class="summary-card">
        <div class="sc-icon">{{ card.icon }}</div>
        <div class="sc-body">
          <p class="sc-name">{{ card.name }}</p>
          <p class="sc-value">
            <span v-if="card.loading" class="sc-loading">로딩 중…</span>
            <span v-else>{{ card.value.toLocaleString() }}</span>
          </p>
          <p v-if="card.change !== null" :class="['sc-change', card.change >= 0 ? 'pos' : 'neg']">
            <span v-if="!card.loading">
              {{ card.change >= 0 ? "▲" : "▼" }}
              {{ Math.abs(card.change).toFixed(1) }}% vs 이전 기간
            </span>
          </p>
        </div>
      </div>
    </div>

    <!-- 성장 차트 -->
    <div class="panel">
      <div class="panel-head">
        <h2 class="panel-title">DAU 추이</h2>
        <div class="group-toggle" v-if="rangeDays > 14">
          <button :class="['grp-btn', { active: groupBy === 'daily' }]" @click="groupBy = 'daily'">
            일별
          </button>
          <button :class="['grp-btn', { active: groupBy === 'weekly' }]" @click="groupBy = 'weekly'">
            주별
          </button>
        </div>
      </div>
      <div v-if="trafficLoading" class="chart-loading">데이터를 불러오는 중…</div>
      <div v-else-if="timelineArray.length === 0" class="chart-loading">표시할 방문 데이터가 없습니다.</div>
      <Chart v-else type="line" :data="chartData" :options="chartOptions" style="height: 260px" />
    </div>

    <!-- 전환 퍼널 -->
    <div class="panel funnel-panel">
      <div class="panel-head">
        <h2 class="panel-title">전환 퍼널</h2>
        <p class="panel-desc">선택 기간 내 단계별 누적 수치</p>
      </div>
      <div class="funnel-list">
        <div v-for="(step, i) in funnelSteps" :key="step.label" class="funnel-row">
          <div class="funnel-label-col">
            <span class="step-num" :style="{ background: step.color }">{{ i + 1 }}</span>
            <span class="step-name">{{ step.label }}</span>
          </div>
          <div class="funnel-bar-col">
            <div class="funnel-track">
              <div class="funnel-fill" :style="{ width: step.pct + '%', background: step.color }"></div>
            </div>
          </div>
          <div class="funnel-stat-col">
            <span class="funnel-count">{{ step.count.toLocaleString() }}</span>
            <span v-if="i > 0" :class="['dropoff-tag', step.dropoff < 0 ? 'up' : 'down']">
              {{ step.dropoff < 0 ? '↑' : '↓' }} {{ step.dropoff < 0 ? '+' : '-' }}{{ Math.abs(step.dropoff).toFixed(1)
                }}% 전 단계 대비 </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useUserStore } from "@/store/user";
import Chart from "primevue/chart";
import StatsTabs from "@/components/StatsTabs.vue";
import { getTrafficStats } from "@/utils/activityTracker";
import { getAllActivityStats, getAllUsers } from "@/utils/read/user";

// ─── 유틸 ──────────────────────────────────────────────────────────────────

function toYMD(date) {
  return date.toISOString().slice(0, 10);
}

function pctChange(curr, prev) {
  if (prev === 0) return 0;
  return ((curr - prev) / prev) * 100;
}

// timeline 객체 { "YYYY-MM-DD": count } → 날짜순 정렬 배열
function timelineToSortedArray(timeline) {
  if (!timeline) return [];
  return Object.entries(timeline)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, count]) => ({ date, count }));
}

// 주별 집계
function groupByWeek(timelineArr) {
  const weeks = [];
  for (let i = 0; i < timelineArr.length; i += 7) {
    const chunk = timelineArr.slice(i, i + 7);
    const sum = chunk.reduce((a, d) => a + d.count, 0);
    const last = chunk[chunk.length - 1];
    weeks.push({
      label: `${chunk[0].date.slice(5)}~${last.date.slice(5)}`,
      count: sum,
    });
  }
  return weeks;
}

// joinDate 파싱 (UserStats.parseDate 기준 - 모든 Firestore 포맷 처리)
function parseJoinDate(date) {
  if (!date) return null;
  try {
    // Firestore Timestamp 객체 (.toDate 메서드)
    if (date.toDate && typeof date.toDate === "function") return date.toDate();

    if (typeof date === "object" && !(date instanceof Date)) {
      // { seconds, nanoseconds } 또는 { _seconds, _nanoseconds }
      const s = date.seconds ?? date._seconds;
      const ns = date.nanoseconds ?? date._nanoseconds ?? 0;
      if (typeof s === "number") {
        const d = new Date(s * 1000 + ns / 1e6);
        if (!isNaN(d.getTime())) return d;
      }
    }

    // ISO string / 일반 날짜 문자열
    if (typeof date === "string" || date instanceof Date) {
      const d = new Date(date);
      if (!isNaN(d.getTime())) return d;
    }

    return null;
  } catch {
    return null;
  }
}

// ─── 색상 ────────────────────────────────────────────────────────────────────
const FUNNEL_COLORS = [
  "#6366f1", // 페이지 뷰
  "#7c3aed", // 공연 조회
  "#a855f7", // 아티스트 조회
  "#ec4899", // 검색/필터
  "#f43f5e", // 로그인
  "#f59e0b", // 찜
  "#f97316", // 공유
  "#10b981", // 티켓 클릭
  "#059669", // 예약 시작
  "#0284c7", // 예약 완료
];

// ─── 컴포넌트 ────────────────────────────────────────────────────────────────
export default {
  name: "GrowthOverview",
  components: { Chart, StatsTabs },

  data() {
    const now = new Date();
    const todayStr = toYMD(now);
    const last7Start = toYMD(new Date(now - 6 * 86400000));

    const months = [];
    for (let i = 0; i < 13; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months.push({ value: val, label: val });
    }

    return {
      today: todayStr,
      activePreset: "last7",
      selectedMonth: "",
      customStart: "",
      customEnd: "",
      startDate: last7Start,
      endDate: todayStr,
      groupBy: "daily",
      monthOptions: months,
      presets: [
        { key: "today", label: "오늘" },
        { key: "last7", label: "최근 7일" },
        { key: "last30", label: "최근 30일" },
      ],
      // 사용자 전체 목록 (신규 가입자 계산용)
      allUsers: [],
      // 실제 트래픽 데이터
      trafficLoading: false,
      currTrafficStats: null,   // { total, byUserType, timeline, ... }
      prevTrafficStats: null,
      // 실제 활동 데이터
      currActivityStats: null,  // { total, loginUserCount, byType, ... }
      prevActivityStats: null,
    };
  },

  created() {
    if (this.userStore.auth !== "admin") {
      this.$router.push("/");
    }
  },

  async mounted() {
    await Promise.all([
      this.loadTrafficData(),
      getAllUsers().then((users) => { this.allUsers = users; }),
    ]);
  },

  watch: {
    startDate() { this.loadTrafficData(); },
    endDate() { this.loadTrafficData(); },
  },

  computed: {
    ...mapStores(useUserStore),

    rangeDays() {
      const ms = new Date(this.endDate) - new Date(this.startDate);
      return Math.round(ms / 86400000) + 1;
    },

    periodLabel() {
      if (this.startDate === this.endDate) return `${this.startDate} (1일)`;
      return `${this.startDate} ~ ${this.endDate}  (${this.rangeDays}일)`;
    },

    // 신규 가입자 (기간 내 joinDate 기준)
    currNewUsers() {
      const start = new Date(this.startDate);
      const end   = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);
      return this.allUsers.filter((u) => {
        const d = parseJoinDate(u.joinDate || u.createdAt);
        return d && d >= start && d <= end;
      }).length;
    },
    prevNewUsers() {
      const days = this.rangeDays;
      const prevEnd   = new Date(new Date(this.startDate) - 86400000);
      const prevStart = new Date(prevEnd.getTime() - (days - 1) * 86400000);
      prevEnd.setHours(23, 59, 59, 999);
      return this.allUsers.filter((u) => {
        const d = parseJoinDate(u.joinDate || u.createdAt);
        return d && d >= prevStart && d <= prevEnd;
      }).length;
    },

    // 트래픽 실제 데이터
    currDau() { return this.currTrafficStats?.total ?? 0; },
    prevDau() { return this.prevTrafficStats?.total ?? 0; },
    currReturning() { return this.currTrafficStats?.byUserType?.loginUser ?? 0; },
    prevReturning() { return this.prevTrafficStats?.byUserType?.loginUser ?? 0; },

    // 찜 수: LIVE_LIKE + ARTIST_LIKE + POST_LIKE
    currLikes() {
      const t = this.currActivityStats?.byType ?? {};
      return (t.LIVE_LIKE ?? 0) + (t.ARTIST_LIKE ?? 0) + (t.POST_LIKE ?? 0);
    },
    prevLikes() {
      const t = this.prevActivityStats?.byType ?? {};
      return (t.LIVE_LIKE ?? 0) + (t.ARTIST_LIKE ?? 0) + (t.POST_LIKE ?? 0);
    },

    summaryCards() {
      return [
        {
          key: "dau",
          name: "DAU (고유 사용자)",
          icon: "👤",
          value: this.currDau,
          change: pctChange(this.currDau, this.prevDau),
          loading: this.trafficLoading,
        },
        {
          key: "returning",
          name: "로그인 재방문",
          icon: "🔄",
          value: this.currReturning,
          change: pctChange(this.currReturning, this.prevReturning),
          loading: this.trafficLoading,
        },
        {
          key: "loginCount",
          name: "로그인 수",
          icon: "🔑",
          value: this.currActivityStats?.byType?.LOGIN ?? 0,
          change: pctChange(
            this.currActivityStats?.byType?.LOGIN ?? 0,
            this.prevActivityStats?.byType?.LOGIN ?? 0,
          ),
          loading: this.trafficLoading,
        },
        {
          key: "likes",
          name: "찜 수",
          icon: "💖",
          value: this.currLikes,
          change: pctChange(this.currLikes, this.prevLikes),
          loading: this.trafficLoading,
        },
        {
          key:     "newUsers",
          name:    "신규 가입자",
          icon:    "✨",
          value:   this.currNewUsers,
          change:  pctChange(this.currNewUsers, this.prevNewUsers),
          loading: false,
        },
        {
          key:     "pageView",
          name:    "홈화면 조회",
          icon:    "📄",
          value:   this.currActivityStats?.byType?.PAGE_VIEW ?? 0,
          change:  pctChange(
            this.currActivityStats?.byType?.PAGE_VIEW ?? 0,
            this.prevActivityStats?.byType?.PAGE_VIEW ?? 0,
          ),
          loading: this.trafficLoading,
        },
        {
          key:     "dauAvg",
          name:    "DAU 평균",
          icon:    "📊",
          value:   this.dauStats.avg,
          change:  pctChange(this.dauStats.avg, this.prevDauStats.avg),
          loading: this.trafficLoading,
        },
        {
          key:     "dauMax",
          name:    "DAU 최대",
          icon:    "📈",
          value:   this.dauStats.max,
          change:  pctChange(this.dauStats.max, this.prevDauStats.max),
          loading: this.trafficLoading,
        },
        {
          key:     "dauMin",
          name:    "DAU 최저",
          icon:    "📉",
          value:   this.dauStats.min,
          change:  pctChange(this.dauStats.min, this.prevDauStats.min),
          loading: this.trafficLoading,
        },
      ];
    },

    // 트래픽 timeline → 정렬된 배열
    timelineArray() {
      return timelineToSortedArray(this.currTrafficStats?.timeline);
    },

    dauStats() {
      const counts = this.timelineArray.map((d) => d.count);
      if (counts.length === 0) return { avg: 0, max: 0, min: 0 };
      const avg = Math.round(counts.reduce((a, b) => a + b, 0) / counts.length);
      const max = Math.max(...counts);
      const min = Math.min(...counts);
      return { avg, max, min };
    },
    prevDauStats() {
      const arr = timelineToSortedArray(this.prevTrafficStats?.timeline);
      const counts = arr.map((d) => d.count);
      if (counts.length === 0) return { avg: 0, max: 0, min: 0 };
      const avg = Math.round(counts.reduce((a, b) => a + b, 0) / counts.length);
      const max = Math.max(...counts);
      const min = Math.min(...counts);
      return { avg, max, min };
    },

    chartData() {
      const useWeekly = this.groupBy === "weekly" && this.rangeDays > 14;
      let labels, values;

      if (useWeekly) {
        const weeks = groupByWeek(this.timelineArray);
        labels = weeks.map((w) => w.label);
        values = weeks.map((w) => w.count);
      } else {
        labels = this.timelineArray.map((d) => d.date.slice(5));
        values = this.timelineArray.map((d) => d.count);
      }

      return {
        labels,
        datasets: [
          {
            label: "DAU",
            data: values,
            borderColor: "#6366f1",
            backgroundColor: "rgba(99,102,241,0.07)",
            fill: true,
            tension: 0.4,
            pointRadius: this.rangeDays <= 14 ? 4 : 2,
            pointHoverRadius: 6,
            borderWidth: 2,
          },
        ],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `  DAU: ${ctx.parsed.y.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            grid: { color: "#f1f5f9" },
            ticks: { font: { size: 11 }, color: "#94a3b8" },
          },
          y: {
            grid: { color: "#f1f5f9" },
            ticks: { font: { size: 11 }, color: "#94a3b8" },
            beginAtZero: false,
          },
        },
      };
    },

    funnelSteps() {
      const t = this.currActivityStats?.byType ?? {};
      const steps = [
        {
          label: "홈화면 조회",
          count: this.currTrafficStats?.total ?? 0,
        },
        {
          label: "공연 조회",
          count: t.LIVE_VIEW ?? 0,
        },
        {
          label: "아티스트 조회",
          count: t.ARTIST_VIEW ?? 0,
        },
        {
          label: "검색 / 필터",
          count: (t.SEARCH ?? 0) + (t.FILTER_APPLY ?? 0) + (t.DATE_FILTER ?? 0),
        },
        {
          label: "공유",
          count: (t.LIVE_SHARE ?? 0) + (t.ARTIST_SHARE ?? 0) + (t.POST_SHARE ?? 0) + (t.VIDEO_SHARE ?? 0),
        },
        {
          label: "로그인",
          count: t.LOGIN ?? 0,
        },
        {
          label: "찜 클릭",
          count: (t.LIVE_LIKE ?? 0) + (t.ARTIST_LIKE ?? 0) + (t.POST_LIKE ?? 0),
        },
        {
          label: "공연 / 아티스트 등록",
          count: (t.LIVE_CREATE ?? 0) + (t.ARTIST_CREATE ?? 0),
        },
        {
          label: "예매하기 클릭",
          count: t.TICKET_CLICK ?? 0,
        },
        {
          label: "티켓 예매",
          count: t.RESERVATION_COMPLETE ?? 0,
        },
      ];
      const top = steps[0].count || 1;
      return steps.map((s, i) => ({
        label: s.label,
        count: s.count,
        pct: Math.round((s.count / top) * 100),
        color: FUNNEL_COLORS[i],
        dropoff: i > 0 && steps[i - 1].count > 0
          ? ((steps[i - 1].count - s.count) / steps[i - 1].count) * 100
          : 0,
      }));
    },
  },

  methods: {
    async loadTrafficData() {
      this.trafficLoading = true;
      try {
        const days = this.rangeDays;
        const prevEnd = new Date(new Date(this.startDate) - 86400000);
        const prevStart = new Date(prevEnd.getTime() - (days - 1) * 86400000);
        const prevEndStr = toYMD(prevEnd);
        const prevStartStr = toYMD(prevStart);

        const [currT, prevT, currA, prevA] = await Promise.all([
          getTrafficStats({ startDate: this.startDate, endDate: this.endDate }),
          getTrafficStats({ startDate: prevStartStr, endDate: prevEndStr }),
          getAllActivityStats({ startDate: this.startDate, endDate: this.endDate }),
          getAllActivityStats({ startDate: prevStartStr, endDate: prevEndStr }),
        ]);

        this.currTrafficStats = currT.stats ?? currT;
        this.prevTrafficStats = prevT.stats ?? prevT;
        this.currActivityStats = currA;
        this.prevActivityStats = prevA;
      } catch (e) {
        console.error("트래픽 데이터 로드 오류:", e);
      } finally {
        this.trafficLoading = false;
      }
    },

    applyPreset(key) {
      this.activePreset = key;
      this.selectedMonth = "";
      this.customStart = "";
      this.customEnd = "";
      const now = new Date();
      const todayStr = toYMD(now);
      if (key === "today") {
        this.startDate = todayStr;
        this.endDate = todayStr;
      } else if (key === "last7") {
        this.startDate = toYMD(new Date(now - 6 * 86400000));
        this.endDate = todayStr;
      } else if (key === "last30") {
        this.startDate = toYMD(new Date(now - 29 * 86400000));
        this.endDate = todayStr;
      }
      if (this.rangeDays <= 14) this.groupBy = "daily";
    },

    applyMonth() {
      if (!this.selectedMonth) return;
      this.activePreset = null;
      this.customStart = "";
      this.customEnd = "";
      const [y, m] = this.selectedMonth.split("-").map(Number);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      const now = new Date();
      this.startDate = toYMD(start);
      this.endDate = toYMD(end > now ? now : end);
    },

    applyCustom() {
      if (!this.customStart || !this.customEnd) return;
      if (this.customStart > this.customEnd) return;
      this.activePreset = null;
      this.selectedMonth = "";
      this.startDate = this.customStart;
      this.endDate = this.customEnd;
    },
  },
};
</script>

<style scoped>
/* ── 레이아웃 ─────────────────────────────────────────────── */
.container {
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 15px;
  min-height: 100vh;
}

.go-header {
  padding: 25px;
  margin-bottom: 20px;
}

.go-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.go-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

/* ── 날짜 필터 바 ───────────────────────────────────────────── */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.filter-presets {
  display: flex;
  gap: 6px;
}

.preset-btn {
  height: 32px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}

.preset-btn:hover {
  background: #f8fafc;
  color: #334155;
}

.preset-btn.active {
  background: #1e293b;
  color: #fff;
  border-color: #1e293b;
}

.filter-sep {
  width: 1px;
  height: 24px;
  background: #e2e8f0;
  flex-shrink: 0;
}

.filter-select,
.date-input {
  height: 32px !important;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #334155;
  background: #fff;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  box-sizing: border-box;
}

.filter-select:focus,
.date-input:focus {
  outline: none;
  border-color: #6366f1;
}

.filter-custom {
  display: flex;
  align-items: center;
  gap: 6px;
}

.range-sep {
  color: #94a3b8;
  font-size: 0.85rem;
}

.apply-button {
  height: 32px;
  width: 80px;
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: background 0.15s;
  box-sizing: border-box;
}

.apply-button:hover {
  background: #4f46e5;
}

.apply-button:disabled {
  background: #c7d2fe;
  cursor: not-allowed;
}

/* ── 기간 배지 ──────────────────────────────────────────────── */
.period-badge {
  display: inline-block;
  padding: 4px 12px;
  background: #f1f5f9;
  border-radius: 20px;
  font-size: 0.78rem;
  color: #64748b;
  margin-bottom: 16px;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* ── 요약 카드 ──────────────────────────────────────────────── */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summary-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}

.sc-icon {
  font-size: 1.5rem;
  line-height: 1;
  flex-shrink: 0;
}

.sc-body {
  flex: 1;
  min-width: 0;
}

.sc-name {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 0 0 4px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sc-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
  line-height: 1.2;
}

.sc-change {
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0;
}

.sc-change.pos {
  color: #10b981;
}

.sc-change.neg {
  color: #ef4444;
}

.sc-change-empty {
  color: #cbd5e1;
  font-size: 0.72rem;
}

/* ── 패널 (차트 / 퍼널) ─────────────────────────────────────── */
.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.panel-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.panel-desc {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0;
}

/* ── 로딩 ────────────────────────────────────────────────────── */
.sc-loading {
  color: #94a3b8;
  font-size: 0.9rem;
  font-weight: 400;
}

.chart-loading {
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.875rem;
}

/* ── 그룹 토글 ──────────────────────────────────────────────── */
.group-toggle {
  display: flex;
  gap: 4px;
}

.grp-btn {
  padding: 4px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.15s;
}

.grp-btn:hover {
  background: #f8fafc;
}

.grp-btn.active {
  background: #1e293b;
  color: #fff;
  border-color: #1e293b;
}

/* ── 퍼널 ────────────────────────────────────────────────────── */
.funnel-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.funnel-row {
  display: grid;
  grid-template-columns: 160px 1fr 150px;
  align-items: center;
  gap: 12px;
}

.funnel-label-col {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}

.step-name {
  font-size: 0.85rem;
  color: #334155;
  font-weight: 600;
  white-space: nowrap;
}

.funnel-bar-col {
  flex: 1;
}

.funnel-track {
  height: 18px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
}

.funnel-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease;
  min-width: 4px;
}

.funnel-stat-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.funnel-count {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e293b;
}

.dropoff-tag {
  font-size: 0.72rem;
  font-weight: 600;
}

.dropoff-tag.down {
  color: #ef4444;
}

.dropoff-tag.up {
  color: #6366f1;
}

/* ── 반응형 ──────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .funnel-row {
    grid-template-columns: 1fr;
  }

  .funnel-stat-col {
    align-items: flex-start;
  }

  .filter-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-sep {
    display: none;
  }
}

@media (max-width: 480px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }

  .filter-custom {
    flex-wrap: wrap;
  }
}
</style>
